import { Element, useEditor, useNode } from "@craftjs/core";
import { Popover } from "@headlessui/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { Schedule } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
  BlockPaddingWrapper,
} from "@/components/editor/shared/block-wrapper";
import EmptyItem from "@/components/editor/shared/schedules/empty-item-component";
import Filter from "@/components/editor/shared/schedules/filter-component";
import { Notification } from "@/components/editor/shared/schedules/notification-component";
import PartsTagsDisplay from "@/components/editor/shared/schedules/parts-tags-display-component";
import SyncedScheduleSettings from "@/components/editor/shared/schedules/schedule-settings-component";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@/components/editor/shared/schedules/schedule-tab";
import { SearchInput } from "@/components/editor/themes/shared/search-input";
import useTheme from "@/components/editor/themes/use-theme";
import { getGroupedSchedules } from "@/features/schedules/schedules-reducer";
import { useScheduleFilter } from "@/hooks/use-schedule-filter";
import { useSchedulesWithEventTimezone } from "@/hooks/use-schedules-with-event-timezone";
import useViewport from "@/hooks/use-viewport";

import {
  FilterWrapper,
  StyledMenuContent,
  Title,
} from "./default-schedule-component-styles";
import DefaultScheduleDisplay from "./default-schedule-display-component";

type SyncedDefaultScheduleProps = {
  title: string;
  schedules?: Schedule[];
};

export default function SyncedDefaultSchedule(
  props: SyncedDefaultScheduleProps
) {
  const {
    id: nodeId,
    connectors: { connect },
  } = useNode();

  const dashboardSchedules = useSelector(getGroupedSchedules);
  const groupedSchedules = props.schedules ?? dashboardSchedules;

  const isEmpty = groupedSchedules.length === 0;
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));

  const viewport = useViewport();
  const { currentTheme } = useTheme();
  const { t } = useTranslation();
  const isMobile = React.useMemo(() => viewport === "mobile", [viewport]);
  const {
    searchValue,
    setSearchValue,
    selectedTags,
    handleTagSelection,
    selectedParts,
    handlePartSelection,
    clearAllSelections,
    isMenuOpen,
    toggleMenu,
    menuRef,
    filters,
    schedules,
  } = useScheduleFilter(groupedSchedules);

  const schedulesWithEventTimezone = useSchedulesWithEventTimezone(schedules);

  const renderSearchBar = React.useCallback(
    () => (
      <SearchInput
        aria-label={t("schedule.search-segments")}
        label={t("schedule.search-segments")}
        onClear={() => setSearchValue("")}
        onChange={setSearchValue}
        value={searchValue}
      />
    ),
    [searchValue, setSearchValue, t]
  );

  if (!enabled && isEmpty) return null;

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={nodeId}
      data-testid="schedule-component"
    >
      <BlockPaddingWrapper>
        <BlockMaxWidthWrapper>
          <Title>
            <Element
              id="title"
              is={Text}
              text={props.title}
              element="h2"
              pattern="headingTwo"
            />
            <div className="ml-2 flex items-start">
              <FilterWrapper>
                <Popover as="div" className="filter-menu" ref={menuRef}>
                  <Filter
                    id="filter-options"
                    label="Filter"
                    totalFiltersSelected={
                      selectedTags.length + selectedParts.length
                    }
                    onClick={toggleMenu}
                    isToggled={isMenuOpen}
                  />
                  {isMenuOpen && (
                    <StyledMenuContent
                      $withBorder={!isMobile}
                      $editing={enabled}
                      role="menu"
                      aria-labelledby="filter-options"
                    >
                      <PartsTagsDisplay
                        onClickPart={handlePartSelection}
                        onClickTag={handleTagSelection}
                        parts={filters.partOptions}
                        selectedParts={selectedParts}
                        selectedTags={selectedTags}
                        tags={filters.tagOptions}
                        onClose={toggleMenu}
                        onClear={clearAllSelections}
                      />
                    </StyledMenuContent>
                  )}
                </Popover>
              </FilterWrapper>
              {!isMobile && renderSearchBar()}
            </div>
          </Title>
        </BlockMaxWidthWrapper>
        <Tabs defaultIndex={0}>
          <TabList>
            {schedulesWithEventTimezone.map((schedule) => (
              <Tab key={schedule.date}>
                {schedule.date}
                {!schedule.isNoFilters && (
                  <Notification
                    className={currentTheme.kind}
                    aria-label={t("schedule.total-search-results", {
                      total_search: schedule.count,
                    })}
                    role="alert"
                  >
                    {schedule.count}
                  </Notification>
                )}
              </Tab>
            ))}
          </TabList>
          {isMobile && renderSearchBar()}
          <BlockMaxWidthWrapper>
            <TabPanels>
              {enabled && isEmpty && (
                <SyncedScheduleSettings type="synced" minimal />
              )}
              {schedulesWithEventTimezone.map((schedule) => (
                <TabPanel key={schedule.date}>
                  {schedule.count === 0 ? (
                    <div className="py-6">
                      <EmptyItem
                        includesSearch={!!searchValue}
                        partsCount={selectedParts.length}
                        tagsCount={selectedTags.length}
                      />
                    </div>
                  ) : (
                    <DefaultScheduleDisplay schedules={schedule.items} />
                  )}
                </TabPanel>
              ))}
            </TabPanels>
          </BlockMaxWidthWrapper>
        </Tabs>
      </BlockPaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

SyncedDefaultSchedule.craft = {
  custom: {
    displayName: "Synced Schedule",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: () => SyncedScheduleSettings({ type: "synced" }),
  },
};
